import { makeStyles } from '@mui/styles';
import { BREAKPOINTS } from '@styles/breakpoints';
import { COLORS } from '@styles/colors';

export const useStyles = makeStyles({
  root: {
    height: '100%',
  },
  rootVirtualized: {
    height: 'calc(100vh - 64px)',
    marginBottom: -20,
    minWidth: '1000px',

    [BREAKPOINTS.MOBILE]: {
      height: 'calc(100vh - 114px)',
    },
  },
  tableHeaderRow: {
    '& td': {
      paddingLeft: 4,
      paddingRight: 4,
    },
    '& td:nth-child(1)': {
      paddingLeft: 12,
    },
    '& td:last-child': {
      paddingRight: 0,
    },

    [BREAKPOINTS.MOBILE]: {
      display: 'none',
    },
  },
  tableDataRow: {
    transition: 'background-color 0.1s',
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: COLORS.ACTION.HIGHLIGHT,
    },
    height: 70,

    '& td': {
      borderBottom: `1px solid ${COLORS.DIVIDER}`,
      verticalAlign: 'middle',
    },
    '&:last-child': {
      '& td': {
        borderBottom: 'none',
      },
    },

    [BREAKPOINTS.MOBILE]: {
      display: 'grid',
      height: 'auto',
      position: 'relative',
      backgroundColor: COLORS.WHITE,
      borderRadius: 8,
      boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.1)',
      padding: 16,
      width: '98%',
      marginBottom: 4,
      gridTemplateColumns: '1fr 1fr 1fr',
      gridTemplateRows: 'auto auto auto',
      gap: '0px 8px',
      gridTemplateAreas: `
        "member member member"
        "month peers clients"
        "status status status"`,

      '& td': {
        borderBottom: 0,
      },

      '& td:nth-child(1)': {
        gridArea: 'member',
      },
      '& td:nth-child(2)': {
        gridArea: 'month',

        '& button': {
          width: '100%',
        },
      },
      '& td:nth-child(3)': {
        position: 'absolute',
        top: 16,
        right: 16,

        '& div': {
          fontSize: 12,
          fontWeight: 500,
        },
      },
      '& td:nth-child(4)': {
        display: 'none',
      },
      '& td:nth-child(5)': {
        gridArea: 'clients',

        '& button': {
          width: '100%',
        },
      },
      '& td:nth-child(6)': {
        gridArea: 'peers',

        '& button': {
          width: '100%',
        },
      },
      '& td:nth-child(7)': {
        gridArea: 'status',
        paddingTop: 12,
        '& > div': {
          justifyContent: 'start',
        },
      },
    },
  },
  cellBase: {
    paddingLeft: 4,
    paddingRight: 4,
  },
  cellMember: {
    width: 195,
    maxWidth: 195,
    paddingLeft: 0,
    paddingRight: 4,
    overflow: 'hidden',
    '& p': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  cellMonth: {
    width: 95,
    maxWidth: 95,
    paddingLeft: 4,
    paddingRight: 4,
  },
  cellEopIndicator: {
    width: 20,
    maxWidth: 20,
    padding: 0,
    textAlign: 'center' as const,
  },
  cellStatus: {
    width: 208,
    maxWidth: 208,
    paddingLeft: 4,
    paddingRight: 4,
    '& > *': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  cellManager: {
    width: 175,
    minWidth: 175,
    maxWidth: 175,
    paddingLeft: 4,
    paddingRight: 4,

    '& button': {
      padding: '6px',
    },
  },
  cellDepartment: {
    width: 140,
    maxWidth: 140,
    paddingLeft: 4,
    paddingRight: 4,
    '& > *': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  cellLocation: {
    width: 68,
    maxWidth: 68,
    paddingLeft: 4,
    paddingRight: 4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cellReviewers: {
    width: 100,
    paddingLeft: 4,
    paddingRight: 4,
  },
  cellActions: {
    width: 140,
    maxWidth: 140,
    paddingLeft: 4,
    paddingRight: 0,
  },
  personNameSkeleton: {
    fontSize: '14px',
    fontWeight: '500',
    margin: 0,
    textDecoration: 'none',
    width: 160,
  },
  positionSkeleton: {
    fontSize: '13px',
    fontWeight: '400',
    color: COLORS.TEXT.SECONDARY,
    margin: '2px 0 0',
    width: 80,
  },
  personBlock: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: COLORS.TEXT.PRIMARY,
    padding: '4px 12px',
    gap: 8,
    borderRadius: 12,
    transition: 'all 0.2s',
    minWidth: 0,
    overflow: 'hidden',
    flex: 1,

    [BREAKPOINTS.MOBILE]: {
      padding: 0,

      '& > div': {
        alignItems: 'center',
      },
    },

    '&:hover': {
      color: COLORS.ACTION.PRIMARY,

      '& > div > div:first-child': {
        transform: 'scale(1.05)',
      },
    },
  },
  personInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  personAvatar: {
    width: 40,
    height: 40,
    borderRadius: 14,
    objectFit: 'cover',
  },
  meterSkeleton: {
    margin: '0 auto',
    width: '70%',
    height: 60,
  },
  memberBlock: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',

    '& input': {
      display: 'block',
      width: 20,
      height: 20,
      cursor: 'pointer',
    },

    [BREAKPOINTS.MOBILE]: {
      padding: '0 0 12px',
    },
  },
  starting: {
    background:
      'linear-gradient(90deg, #A2DA8B 5.88%, #D1ED97 17.06% , #A2DA8B 29.12%, #A2DA8B 55.88%, #D1ED97 67.06%, #A2DA8B 79.12%)',
    backgroundSize: '200%',
    animation: '$startingBg 3s infinite linear',
  },
  '@keyframes startingBg': {
    '0%': { backgroundPositionX: '100%' },
    '50%': { backgroundPositionX: '0%' },
    '100%': { backgroundPositionX: '100%' },
  },
  backdrop: {
    position: 'absolute',
    cursor: 'auto',
    backgroundColor: COLORS.WHITE,
    opacity: 0.3,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 5,
  },
  noClickable: {
    cursor: 'default',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  canceling: {
    background:
      'linear-gradient(90deg, #f23535 5.88%, #fc8383 17.06% , #f23535 29.12%, #f23535 55.88%, #fc8383 67.06%, #f23535 79.12%)',
    backgroundSize: '200%',
    animation: '$startingBg 3s infinite linear',
  },
  titleText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  managerName: {
    color: COLORS.TEXT.PRIMARY,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  projectName: {
    color: COLORS.TEXT.PRIMARY,
  },
  departmentName: {
    color: COLORS.TEXT.SECONDARY,
  },
});
